import { useNavigate, useLocation } from '@tanstack/react-router';
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
} from '@mui/material';
import { ExpandLess, ExpandMore, Circle as CircleIcon } from '@mui/icons-material';
import { useMenu } from '../../features/menu';
import type { MenuItem as MenuItemType } from '../../features/menu/types';
import type { MouseEvent } from 'react';

type MenuItemProps = {
  item: MenuItemType;
  depth?: number;
  onClose?: () => void;
};

export const MenuItem = ({ item, depth = 0, onClose }: MenuItemProps) => {
  const { toggle, isExpanded } = useMenu();
  const navigate = useNavigate();
  const location = useLocation();

  const hasChildren = item.childs && item.childs.length > 0;
  const expanded = isExpanded(item.id);

  // Check if this menu item is active
  // 1. Exact match: item.link === current path (normalized)
  // 2. Legacy grid format: /grid/:id
  // 3. Current path is a child of this menu item (e.g., detail page /projects/123 when menu item is /projects/)
  const isActive = (() => {
    if (!item.link) return false;

    const normalizePath = (path: string) => {
      return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
    };

    const currentPath = normalizePath(location.pathname);
    const itemPath = normalizePath(item.link);

    // Exact match (using normalized paths)
    if (currentPath === itemPath) {
      return true;
    }

    // Check if current path is a child of this menu item
    // Example: menu item is /employees, current path is /employees/123 or /employees/add
    if (currentPath.startsWith(itemPath + '/')) {
      return true;
    }

    return false;
  })();

  const handleClick = (event: MouseEvent) => {
    if (hasChildren) {
      toggle(item.id);
    } else if (item.link) {
      if (event.button === 1 || event.metaKey || event.ctrlKey) {
        return;
      }

      event.preventDefault();
      navigate({ to: item.link });
      onClose?.();
    } else {
      console.warn('Menu item has no link:', item);
    }
  };

  const renderIcon = () => {
    if (!item.icon) return null;

    if (item.icon.includes('fa-')) {
      return <i className={item.icon} style={{ fontSize: '1.25rem' }} />;
    }

    return <CircleIcon fontSize="small" />;
  };

  return (
    <>
      <ListItem disablePadding sx={{ pl: depth * 2 }}>
        <ListItemButton
          component={!hasChildren && item.link ? 'a' : 'div'}
          href={!hasChildren && item.link ? item.link : undefined}
          onClick={handleClick}
          selected={isActive}
          sx={{
            minHeight: 48,
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
            },
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {item.icon && (
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: isActive ? 'primary.contrastText' : 'inherit',
              }}
            >
              {renderIcon()}
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.caption}
            primaryTypographyProps={{
              fontSize: depth > 0 ? '0.875rem' : '1rem',
              fontWeight: isActive ? 600 : 400,
            }}
          />
          {hasChildren && (
            <Box sx={{ ml: 1 }}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
          )}
        </ListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.childs.map((child) => (
              <MenuItem key={child.id} item={child} depth={depth + 1} onClose={onClose} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default MenuItem;
